import {
  ENV_NAME,
  SORTBY_OPTIONS,
  USER_GAME_EXP_LAST_UPDATED_THRESHOLD
} from './constants';
import { differenceInYears, isValid, parseISO } from 'date-fns';

import { Game } from '../models/game';
import { GameExperience } from '../models/survey';
import { get } from 'lodash';

export const sortAlphaBy = (key?: string) => (a: any, b: any) =>
  (key ? get(a, key) : a).localeCompare(key ? get(b, key) : b);

export const createOptions = (
  data: Array<{ id: any; name: string; groupId?: number; index?: number }>,
  sortByOption?: number,
  sortAsc?: boolean
) => {
  const options = data
    ? data.map((option) => ({
        value: option.id,
        label: option.name,
        groupId: option.groupId ?? 0,
        index: option.index ?? 0
      }))
    : [];
  if (sortByOption == SORTBY_OPTIONS.Id && sortAsc) {
    options.sort((a, b) => a.value - b.value);
  } else if (sortByOption == SORTBY_OPTIONS.Id && !sortAsc) {
    options.sort((a, b) => b.value - a.value);
  } else if (sortByOption == SORTBY_OPTIONS.Name) {
    options.sort((a, b) =>
      a.groupId == 0 && b.groupId == 0
        ? a.label < b.label
          ? -1
          : 1
        : a.groupId == 0 || b.groupId == 0
        ? a.label < b.label
          ? -1
          : 1
        : a.groupId == b.groupId
        ? a.index < b.index
          ? -1
          : 1
        : a.label < b.label
        ? -1
        : 1
    );
  }

  return options;
};

export const createStudioOptions = (
  data: Array<{ id: any; name: string; country: { id: any; name: string } }>
) => {
  const options = data.reduce((groups: any, studio: any) => {
    if (!studio) return groups;
    const { country } = studio;
    groups.find((g: any) => g.label === country.name)
      ? groups
          .find((g: any) => g.label === country.name)
          .options.push({
            value: studio.id,
            label: studio.name
          })
      : groups.push({
          label: country.name,
          options: [
            {
              value: studio.id,
              label: studio.name
            }
          ]
        });
    return groups;
  }, []);

  options.sort((a: any, b: any) => (a.label < b.label ? -1 : 1));

  options.map((o: any) =>
    o.options.sort((a: any, b: any) => (a.name < b.name ? -1 : 1))
  );

  return options;
};

export const timeInYears = (date: string) => {
  const parsedDate = parseISO(date);
  return isValid(parsedDate) ? differenceInYears(new Date(), parsedDate) : '';
};

export const getCookie = (name: string) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    while (cookie.startsWith(' ')) {
      cookie = cookie.slice(1, cookie.length);
    }
    if (cookie.startsWith(nameEQ)) {
      return cookie.slice(nameEQ.length, cookie.length);
    }
  }
};

export const RES = {
  name: /@[^@]+$/
};

export const toShortName = (str: string) => str.replace(RES.name, '');

// Generic function for setting the app title
export const setTitle = (title: string) =>
  (document.title = `${title} - ${
    ENV_NAME === 'production' ? 'Playtest' : `Nemo - ${ENV_NAME}`
  }`);

/*https://codesandbox.io/s/scroll-to-input-formik-failed-submission-gnehr?file=/src/App.js:1217-1235*/
export const getFieldErrorNames = (formikErrors: any) => {
  const transformObjectToDotNotation = (obj: any, result = [] as any) => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (!value) return;

      if (typeof value === 'object') {
        transformObjectToDotNotation(value, result);
      } else {
        result!.push(key);
      }
    });

    return result;
  };

  return transformObjectToDotNotation(formikErrors);
};

export const scrollToBannerBottom = () => {
  const element = document.querySelector(`.profile .page__header`);
  if (!element) return;
  window.scroll({
    behavior: 'smooth',
    top: element.getBoundingClientRect().height
  });
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

export const getGameDisplayString = (game: Game | GameExperience) => {
  if ('game' in game) {
    return `${game.game.name} ${
      game.game.releaseYear ? `(${game.game.releaseYear})` : ''
    }`;
  } else {
    return `${game.name} ${game.releaseYear ? `(${game.releaseYear})` : ''}`;
  }
};

export const needsGameExperienceUpdate = (gameExperience: any): boolean => {
  if (!gameExperience.lastUpdate || !gameExperience.updateRequired) {
    return false;
  }

  const thresholdDate = new Date();
  thresholdDate.setMonth(
    thresholdDate.getMonth() - USER_GAME_EXP_LAST_UPDATED_THRESHOLD
  );
  const lastUpdateDate = new Date(gameExperience.lastUpdate);

  return lastUpdateDate < thresholdDate;
};

export const allSurveyGamesNeedUpdate = (
  candidateGameExperiences: GameExperience[],
  surveyGames: any[]
): boolean => {
  if (!candidateGameExperiences || !surveyGames || surveyGames.length === 0) {
    return false;
  }

  const gamesRequiringUpdate = surveyGames.filter((sg) => sg.updateRequired);

  if (gamesRequiringUpdate.length === 0) {
    return false;
  }

  return gamesRequiringUpdate.every((sg) => {
    const gameExp = candidateGameExperiences.find(
      (cge) => cge.igdbGameId === sg.game.igdbGameId
    );
    return !gameExp || needsGameExperienceUpdate(gameExp);
  });
};
